import { COOKIE_CONSENT_TOAST_ID } from '@features/cookieConsent/model/toastAction.js';
import { isTrackLine } from '@features/dataViewer/trackSelection.js';
import type { PickMode } from '@features/routePlanner/model/actions.js';
import type { Track } from '@features/tracking/model/types.js';
import { isDrawTool } from '@shared/toolDefinitions.js';
import { createSelector } from 'reselect';
import marker from '@/images/cursors/marker.svg';
import pencil from '@/images/cursors/pencil.svg';
import type { Tool } from '../store/actions.js';
import type { RootState } from '../store/store.js';

// Whether a tool's toolbar is on the screen, whichever slot it is open in.
export const isToolOpen = (state: RootState, tool: Tool): boolean =>
  state.main.mapTool === tool || state.main.panelTools.includes(tool);

// Every open tool, the map-click one first — memoized, so subscribing to it
// doesn't re-render on every unrelated action the way a fresh array would.
export const openToolsSelector = createSelector(
  (state: RootState) => state.main.mapTool,
  (state: RootState) => state.main.panelTools,
  (mapTool, panelTools): Tool[] =>
    mapTool ? [mapTool, ...panelTools] : panelTools,
);

// The open tool as far as map clicks go — i.e. the one that owns them; null
// while only toolbar-only tools are open.
export const activeModeSelector = (state: RootState): Tool | null =>
  state.main.mapTool;

// The open draw-* tool (the three of them share one menu, and the map-click
// slot they all take).
export const openDrawToolSelector = (state: RootState): Tool | null =>
  isDrawTool(state.main.mapTool) ? state.main.mapTool : null;

export const mapLayersSelector = (state: RootState): string[] =>
  state.map.layers;

export const selectingHomeLocationSelector = (state: RootState): boolean =>
  state.homeLocation.selectingHomeLocation !== false;

export const routePlannerPickModeSelector = (
  state: RootState,
): PickMode | null => state.routePlanner.pickMode;

export const galleryPickingPositionForIdSelector = (
  state: RootState,
): number | null => state.gallery.pickingPositionForId;

export const galleryShowPositionSelector = (state: RootState): boolean =>
  state.gallery.showPosition;

export const drawingLineSelector = (state: RootState): boolean =>
  state.drawingLines.drawing;

export const mapAreaSelectingSelector = (state: RootState): boolean =>
  state.mapArea.selecting !== null;

export const toposcopePickingCenterSelector = (state: RootState): boolean =>
  state.toposcope.pickingCenter;

/** A library map is previewed; unlike the picks, it switches no other map. */
export const mapLibraryPreviewingSelector = (state: RootState): boolean =>
  state.mapLibrary.preview !== null;

export const panoramaPickingSelector = (state: RootState): boolean =>
  state.panorama.picking !== null;

export const viewshedPickingViewpointSelector = (state: RootState): boolean =>
  state.viewshed.pickingViewpoint;

/**
 * True while the map is given over to a click that says where. Features stay
 * visible but non-interactive, the tools go inert, and `Main` puts the toolbars
 * and the floating panels away. Every such mode belongs here rather than in a
 * list of its own.
 */
export const pickingModeSelector = (state: RootState): boolean =>
  selectingHomeLocationSelector(state) ||
  toposcopePickingCenterSelector(state) ||
  panoramaPickingSelector(state) ||
  viewshedPickingViewpointSelector(state) ||
  galleryPickingPositionForIdSelector(state) !== null ||
  galleryShowPositionSelector(state) ||
  mapAreaSelectingSelector(state) ||
  mapLibraryPreviewingSelector(state);

/**
 * True while the map is given over to a click on a *feature* — joining two
 * lines, aiming a cut. The chrome goes away as it does for a picking mode, but
 * the features stay live: the click these are waiting for is a click on one.
 * The armed mode's own toolbar says what it wants and cancels it.
 */
export const armedModeSelector = (state: RootState): boolean =>
  state.drawingLines.joinWith !== undefined ||
  // The data viewer's toolbar rides its selection, so a mode can never take
  // the chrome away without it.
  (state.main.selection?.type === 'data-viewer' &&
    (state.trackViewer.joinWith !== null || state.trackViewer.splitting));

/** Whether the map is in a mode of its own, whichever kind. */
export const mapModeSelector = (state: RootState): boolean =>
  pickingModeSelector(state) || armedModeSelector(state);

// The active tool as far as map interaction is concerned: the open map-click
// tool, but masked to null while a picking mode owns the map so it goes inert.
// Map-interaction code should read this; menus/processors that want to know
// whether a toolbar is up should use isToolOpen.
export const activeMapToolSelector = (state: RootState): Tool | null =>
  pickingModeSelector(state) ? null : activeModeSelector(state);

export const showGalleryPickerSelector = createSelector(
  activeMapToolSelector,
  mapLayersSelector,
  pickingModeSelector,
  drawingLineSelector,
  (tool, layers, picking, drawingLine) =>
    // The picker takes clicks of its own, so it wants the map to itself.
    !tool && layers.includes('I') && !picking && !drawingLine,
);

export const showGalleryViewerSelector = (state: RootState): boolean =>
  state.gallery.pickingPositionForId === null &&
  state.gallery.activeImageId !== null &&
  !state.gallery.showPosition;

const pencilCursor = `url(${pencil}) 1.5 20, crosshair`;

const markerCursor = `url(${marker}) 13.5 26, crosshair`;

export const mouseCursorSelector = createSelector(
  selectingHomeLocationSelector,
  activeMapToolSelector,
  routePlannerPickModeSelector,
  showGalleryPickerSelector,
  galleryShowPositionSelector,
  (state: RootState) => state.drawingLines.drawing,
  toposcopePickingCenterSelector,
  panoramaPickingSelector,
  viewshedPickingViewpointSelector,
  (
    selectingHomeLocation,
    tool,
    routePlannerPickMode,
    showGalleryPicker,
    galleryShowPosition,
    drawing,
    toposcopePickingCenter,
    panoramaPicking,
    viewshedPickingViewpoint,
  ) => {
    if (galleryShowPosition) {
      return 'auto';
    }

    if (
      selectingHomeLocation ||
      showGalleryPicker ||
      toposcopePickingCenter ||
      panoramaPicking ||
      viewshedPickingViewpoint
    ) {
      return 'crosshair';
    }

    if (drawing) {
      return pencilCursor;
    }

    switch (tool) {
      case 'draw-lines':
        return pencilCursor;

      case 'draw-polygons':
        return pencilCursor;

      case 'map-details':
        return 'help';

      case 'draw-points':
        return markerCursor;

      case 'route-planner':
        return routePlannerPickMode ? 'crosshair' : 'auto';

      default:
        return 'auto';
    }
  },
);

////

export const trackingTracksSelector = (state: RootState): Track[] =>
  state.tracking.tracks;

export const trackingActiveTrackIdSelector = (
  state: RootState,
): string | number | undefined =>
  state.main.selection?.type === 'tracking'
    ? state.main.selection.id
    : undefined;

export const trackingTrackSelector = createSelector(
  trackingTracksSelector,
  trackingActiveTrackIdSelector,
  (trackingTracks, trackingActiveTrackId) =>
    trackingActiveTrackId
      ? trackingTracks.find((t) => t.token === trackingActiveTrackId)
      : undefined,
);

export const selectingModeSelector = (state: RootState): boolean => {
  const mode = activeModeSelector(state);

  return (
    !window.fmEmbedded &&
    !pickingModeSelector(state) &&
    !state.drawingLines.drawing &&
    // selection works when no map-click tool captures the click, except a
    // route-planner that is actively picking points
    (mode === null ||
      (mode === 'route-planner' && state.routePlanner.pickMode === null))
  );
};

export const drawingLinePolys = (state: RootState): boolean => {
  // The open drawing tool captures clicks (to start a line). So does an
  // in-progress drawing (to append points), which a "continue" starts without
  // opening any tool.
  const tool = activeMapToolSelector(state);

  return (
    tool === 'draw-lines' ||
    tool === 'draw-polygons' ||
    // A picking mode owns the map, so an in-progress drawing must go inert there
    // too — mirror the masking `activeMapToolSelector` already applies.
    (state.drawingLines.drawing && !pickingModeSelector(state))
  );
};

export const trackGeojsonIsSuitableForElevationChart = (
  state: RootState,
): boolean =>
  // Any line-like feature (the active track is chosen among them); a
  // `MultiLineString` is a multi-segment recording (an interrupted track).
  (state.trackViewer.trackGeojson?.features ?? []).some(isTrackLine);

export const askingCookieConsentSelector = (state: RootState): boolean =>
  COOKIE_CONSENT_TOAST_ID in state.toasts.toasts;

// Whether `clearMapFeatures` would actually remove anything from the map —
// drives showing the "Clear map" command. Mirrors the slices that reset on
// `clearMapFeatures` (see their reducers), limited to the ones that put
// user-visible features on the map.
export const hasClearableMapFeaturesSelector = (state: RootState): boolean =>
  state.main.selection !== null ||
  state.drawingPoints.points.length > 0 ||
  state.drawingLines.lines.length > 0 ||
  state.routePlanner.points.length > 0 ||
  state.objects.objects.length > 0 ||
  state.changesets.changesets.length > 0 ||
  state.search.selectedResults.length > 0 ||
  state.trackViewer.trackGeojson !== null ||
  state.tracking.trackedDevices.length > 0;
